import { useQuery } from "@tanstack/react-query";
import { useAuth } from "contexts/AuthContext";
import api from "utils/api";
import { historyKeys } from "../historyKeys";
import { OWN_UNDO_SESSION_GC_TIME_MS } from "../../constants";
import { historySubjectIdsOf } from "../../utils/historySubjectIdsOf";
import type { OwnUndoState } from "../../types";
import type { UseOwnUndoStateArgs, UseOwnUndoStateResult } from "./types";

export const useOwnUndoState = ({
  projectId,
  subject,
}: UseOwnUndoStateArgs): UseOwnUndoStateResult => {
  const { user } = useAuth();

  const query = useQuery({
    // The user is part of the key, so another login on the same page starts its own session.
    queryKey: historyKeys.ownUndo(projectId, subject, user?.id ?? null),
    queryFn: ({ client, queryKey }): Promise<OwnUndoState> => {
      if (subject === null) throw new Error("No plan or diagram selected");
      const previous = client.getQueryData<OwnUndoState>(queryKey);
      return api.getOwnUndoState({
        projectId,
        ...historySubjectIdsOf(subject),
        sinceEntryId: previous?.sessionStartEntryId ?? null,
      });
    },
    enabled: subject !== null,
    // Another user's change can block the target at any time; never trust a cached answer.
    staleTime: 0,
    // The cached answer carries the session start, so it outlives short switches to other plans and diagrams.
    gcTime: OWN_UNDO_SESSION_GC_TIME_MS,
  });

  return {
    undo: query.data?.undo ?? null,
    redo: query.data?.redo ?? null,
    isFetching: query.isFetching,
  };
};
